import React from "react";
import { Star, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { reviewService } from "../services/api";

export default function Testimonials() {
  const { t } = useTranslation();
  const [testimonials, setTestimonials] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsResponse, statsResponse] = await Promise.all([
          reviewService.getTestimonials(3), // Get 3 for the grid
          reviewService.getStats()
        ]);

        const testimonialsData = testimonialsResponse.data;
        const statsData = statsResponse.data;
        
        const testimonialsWithAvatars = testimonialsData.map(testimonial => ({
          ...testimonial,
          image: generateAvatar(testimonial.customer_name)
        }));
        
        setTestimonials(testimonialsWithAvatars);
        setStats(statsData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setTestimonials([]);
        setStats({
          verifiedPercentage: 100,
          averageRating: '5.0',
          uniqueCustomers: 2
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const generateAvatar = (name) => {
    if (!name) return "";
    const initials = name
      .split(' ')
      .filter(n => n.length > 0)
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
    
    const colors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = colors[Math.abs(hash) % colors.length];
    
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="20" fill="${color}"/>
        <text x="50%" y="50%" dy=".1em" fill="white" font-size="14" font-weight="bold" text-anchor="middle" dominant-baseline="middle" font-family="system-ui, -apple-system, sans-serif">
          ${initials}
        </text>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  if (loading && !testimonials.length) {
    return (
      <div className="py-24 bg-raya-bg flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-raya-accent"></div>
      </div>
    );
  }

  return (
    <section id="testimonials" className="py-24 bg-raya-bg overflow-hidden font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="flex flex-col items-center mb-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-raya-accent text-raya-bg text-[10px] font-bold uppercase tracking-widest mb-6 shadow-sm">
            <Star className="w-3 h-3 fill-current" />
            {t('testimonials.badge')}
          </div>
          <h2 className="text-4xl md:text-6xl font-serif font-bold text-raya-text-primary mb-6">
            {t('testimonials.title')}
          </h2>
          <p className="max-w-xl text-md text-zinc-500 dark:text-zinc-400 leading-relaxed">
            {t('testimonials.subtitle')}
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
          {testimonials.map((testimonial, idx) => (
            <div
              key={testimonial.id || idx}
              className="flex flex-col review-card hover:shadow-md transition-shadow relative min-h-[320px]"
            >
              {/* Stars and Quote */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-raya-accent fill-current"
                    />
                  ))}
                </div>
                <div className="flex gap-0.5 opacity-10">
                  <div className="w-2.5 h-6 bg-raya-accent rounded-full rotate-[15deg]" />
                  <div className="w-2.5 h-6 bg-raya-accent rounded-full rotate-[15deg]" />
                </div>
              </div>

              {/* Review Text */}
              <div className="flex-grow">
                <p className="text-raya-text-secondary text-[15px] leading-relaxed mb-8">
                  "{testimonial.review_text}"
                </p>
              </div>

              {/* User Info */}
              <div className="flex items-center gap-3 mt-auto pt-6">
                <img
                  src={testimonial.image}
                  alt={testimonial.customer_name}
                  className="w-12 h-12 rounded-full shadow-inner border border-raya-border"
                />
                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-raya-text-primary text-sm">
                      {testimonial.customer_name}
                    </span>
                    <div className="bg-raya-accent rounded-full p-0.5">
                      <CheckCircle2 className="w-2.5 h-2.5 text-black" />
                    </div>
                  </div>
                  <span className="text-[11px] text-raya-text-tertiary font-medium tracking-wide">
                    {t('testimonials.verified_buyer')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Separator */}
        <div className="w-full h-px bg-raya-border mb-24" />

        {/* Community Stats Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-raya-text-primary mb-6">
            {t('testimonials.communityTitle')}
          </h2>
          <p className="text-md text-zinc-500 dark:text-zinc-400">
            {t('testimonials.communityDesc')}
          </p>
        </div>

        {/* Big Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="stats-card text-center group transition-colors">
            <div className="stats-number mb-2 flex items-center justify-center gap-1">
              5.0<Star className="w-8 h-8 fill-current" />
            </div>
            <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-[0.2em]">
              {t('testimonials.customerRating')}
            </div>
          </div>

          <div className="stats-card text-center group transition-colors">
            <div className="stats-number mb-2">
              {stats?.uniqueCustomers || '2'}
            </div>
            <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-[0.2em]">
              {t('testimonials.stats.happyCustomers')}
            </div>
          </div>

          <div className="stats-card text-center group transition-colors">
            <div className="stats-number mb-2">
              {stats?.verifiedPercentage || '100'}%
            </div>
            <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-[0.2em]">
              {t('testimonials.stats.verifiedReviews')}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-raya-border rounded-full text-raya-text-secondary text-xs font-medium shadow-sm">
            <div className="bg-raya-accent-light text-raya-accent rounded-full p-1">
              <CheckCircle2 className="w-3 h-3" />
            </div>
            {t('testimonials.verifiedBadge')}
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-raya-border rounded-full text-raya-text-secondary text-xs font-medium shadow-sm">
            <Star className="w-4 h-4 text-raya-accent fill-current" />
            {t('testimonials.ratingBadge')}
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-raya-border rounded-full text-raya-text-secondary text-xs font-medium shadow-sm">
            <Users className="w-4 h-4 text-raya-accent" />
            {stats?.uniqueCustomers || '2'}+ {t('testimonials.customersBadge')}
          </div>
        </div>
      </div>
    </section>
  );
}

// Icon for checkmark
const CheckCircle2 = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="3" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
